// dueBanner.mjs — 到期动作横幅的判定（SB-117，第一百一十三批）。
//
// ── 为什么是一个纯函数文件，而不是壳里的三行 ────────────────────────────────
// AppShell 的头注写着：**本文件不得再出现第二份路由清单**——xs 断点下五条成员入口
// 全部失联那个洞，根因就是壳里躺过一个 NAV 数组加两个硬写的 MenuItem。同一条纪律
// 适用于这里：一条"什么时候该出声、出多大声"的规则若躺在 JSX 里，它就没有判据，
// 而 SB-117 §3 的验收项恰恰是**"把触发日设成昨天，它必须比未到期的行更显眼"**。
//
// ── 出声的门槛 ──────────────────────────────────────────────────────────────
// **只对「过期」与「就是今天」出声**，未来的不出声。理由是这条横幅要活很久：
// 一条每天都在喊"你三周后有件事"的横幅，两天之内就会被当成背景，
// 而它真正要拦的那一天到来时，没有人会再看它一眼。
//
// 服务端已经把 overdue / dueToday / daysLate 算好随行给出（routes_due.cpp；按每行**自己的帧**
// 的今天算，SB-191），**这里不重算日期**：两处各算一遍"今天是几号"，迟早会在某个时区的某一天分叉
// ——2026-10-04Z 17:13Z 服务端自己就分叉过一次（一条「23:59 UTC」的行被按 Asia/Shanghai 的日子判成迟 1 天）。
export const DUE_SEVERITY = { overdue: 'error', today: 'warning', none: null };

// SB-159（第三百八十二批）口径 3：「取不到」与「没有」必须分得开。到期动作页此前在加载失败时
// ——包括服务端刻意的 503「这不是一张空表」（routes_due.cpp：没有库就不装作查了个空表）——照样印
// 那句绿色的「没有待处理的到期动作」。在守期限的这一页上，一次失败的取数长得与「什么都没到期」
// 一模一样。判定收在这里（纯函数，有单测）：error 就是 error，只有 ready 且确实零行才是 empty。
export function dueListView(status, rows) {
  if (status === 'loading') return 'loading';
  if (status === 'error') return 'error';
  return Array.isArray(rows) && rows.length > 0 ? 'list' : 'empty';
}

// SB-191（2026-10-05Z）：帧标记只在这里写一次。utc 行的触发日带 Z（SOP-11 §3b 的 UTC 日写法），
// local 行裸写；没带 dueFrame 的应答（旧服务端）**不替它造一个帧**——造出来的帧与猜出来的今天
// 是同一种病。页面与壳都从这里取，两处各写一遍迟早漂开。
export function dueOnLabel(row) {
  return row?.dueFrame === 'utc' ? `${row.dueOn}Z` : String(row?.dueOn ?? '');
}

// 谁落定的（第三百五十四批，OQ-E123 Q1：记录上要看得出是工作空间落定的、不是某个人，页面上看得见）。
// 服务端每行给 settledBy：pending 为 null；落定后 {kind: person|workspace, accountId, ref}。这里只把它
// 归成页面要挂的那一枚标签——pending ⇒ null（不挂）；workspace ⇒ 带 ref（报了才带）；person ⇒ 人；
// 其余（旧服务端没给、给了认不出的 kind）⇒ unknown：照实说"未记录"，**不替它编一个落定者**——
// 把一笔说不清的落定默认挂成"人"，正是这条裁决要防的那一种不诚实（与 dueOnLabel 不造帧同一条）。
export function settledByOf(row) {
  if (!row || row.state === 'pending') return null;
  const sb = row.settledBy;
  if (sb && typeof sb === 'object') {
    if (sb.kind === 'workspace') {
      return { kind: 'workspace', ref: typeof sb.ref === 'string' && sb.ref ? sb.ref : null };
    }
    if (sb.kind === 'person') return { kind: 'person', ref: null };
  }
  return { kind: 'unknown', ref: null };
}

// summary(payload) → { severity, count, worst, items, kind, todayUtc, todayLocal }
// payload 是 GET /api/due-actions 的响应；缺字段一律按"没有"处理（读面不该因为
// 一次网络抖动就喊一嗓子，也不该因为它就把已经过期的事藏起来——所以是缺则静默，
// 而不是缺则报警）。
export function dueSummary(payload) {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  const overdue = items.filter((x) => x?.overdue === true);
  const today = items.filter((x) => x?.dueToday === true);
  // SB-191：两个今天只**透传**、不解释——横幅把它们写在句尾，让读的人知道这句"迟了 N 天"
  // 是按哪个午夜算的。旧服务端没给就是 null，不拿 `today` 顶替：那会把 utc 行的日子说错。
  const todays = { todayUtc: payload?.todayUtc ?? null, todayLocal: payload?.todayLocal ?? null };

  if (overdue.length > 0) {
    // 过期优先，且报**最迟的那一条**：一条迟了 12 天的事和一条迟了 1 天的事
    // 挤在同一句话里，读的人会按后者的紧迫程度处理。
    const worst = overdue.reduce((a, b) => ((b?.daysLate ?? 0) > (a?.daysLate ?? 0) ? b : a));
    return {
      severity: DUE_SEVERITY.overdue,
      count: overdue.length,
      worst,
      items: overdue,
      kind: 'overdue',
      ...todays,
    };
  }
  if (today.length > 0) {
    return { severity: DUE_SEVERITY.today, count: today.length, worst: today[0],
             items: today, kind: 'today', ...todays };
  }
  return { severity: DUE_SEVERITY.none, count: 0, worst: null, items: [], kind: 'none', ...todays };
}

// 这条横幅**不可关闭**，而口令提示那条可以——差别是刻意的：
// 口令提示关掉之后，验证码登录照样能进，没有人会因此被挡在门外；
// 而到期动作关掉之后，**那件事就真的没有任何东西会再提起它了**，
// 而这正是 SB-117 立单要消灭的那一种沉默。
export const DUE_BANNER_DISMISSIBLE = false;
